import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/api';
import { sampleTGEs } from '../utils/data';

const AdminPanel: React.FC = () => {
  const [tges, setTges] = useState(sampleTGEs);
  const [selectedTGE, setSelectedTGE] = useState<string>('');
  const [actualFDV, setActualFDV] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadTGEs();
  }, []);

  const loadTGEs = async () => {
    try {
      const response = await ApiService.getTGEs();
      if (response.success && response.data) {
        setTges(response.data);
      }
    } catch (error) {
      console.error('Error loading TGEs:', error);
    }
  };

  const handleSettleTGE = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTGE || !actualFDV) {
      setMessage({ type: 'error', text: 'Please select a TGE and enter the actual FDV' });
      return;
    }

    try {
      setIsLoading(true);
      setMessage(null);

      const response = await ApiService.settleTGE(selectedTGE, parseInt(actualFDV));
      
      if (response.success) {
        setMessage({ type: 'success', text: 'TGE settled successfully! Points have been calculated and distributed.' });
        setSelectedTGE('');
        setActualFDV('');
        await loadTGEs(); // Reload to get updated status
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to settle TGE' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Handle TBD dates (placeholder dates)
    if (dateString === '2025-01-01') {
      return 'TBD';
    }
    
    // Handle dates with time component by extracting just the date part
    let dateToFormat = dateString;
    if (dateString.includes(' ')) {
      dateToFormat = dateString.split(' ')[0];
    }
    
    return new Date(dateToFormat).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Admin Panel</h2>
        <p className="text-gray-300">Manage TGE settlements and game administration</p>
      </div>

      {message && (
        <div className={`rounded-lg p-4 text-center ${
          message.type === 'success' 
            ? 'bg-green-900/20 border border-green-500/30 text-green-400'
            : 'bg-red-900/20 border border-red-500/30 text-red-400'
        }`}>
          {message.text}
        </div>
      )}

      <div className="card">
        <h3 className="text-xl font-bold text-white mb-4">Settle TGE</h3>
        
        <form onSubmit={handleSettleTGE} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select TGE to Settle
            </label>
            <select
              value={selectedTGE}
              onChange={(e) => setSelectedTGE(e.target.value)}
              className="form-input w-full"
              required
            >
              <option value="">Choose a TGE...</option>
              {tges
                .filter(tge => tge.status === 'OPEN')
                .map((tge) => (
                  <option key={tge.id} value={tge.id}>
                    {tge.name} - {formatDate(tge.launchDate)}
                  </option>
                ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Actual FDV (USD)
            </label>
            <input
              type="number"
              value={actualFDV}
              onChange={(e) => setActualFDV(e.target.value)}
              className="form-input w-full"
              placeholder="Enter the actual FDV"
              min="0"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !selectedTGE || !actualFDV}
            className="button-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Settling...' : 'Settle TGE'}
          </button>
        </form>
      </div>

      <div className="card">
        <h3 className="text-xl font-bold text-white mb-4">TGE Status Overview</h3>
        
        <div className="space-y-4">
          {tges.map((tge) => (
            <div key={tge.id} className="border border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-lg font-semibold text-white">{tge.name}</h4>
                <span className={`px-2 py-1 rounded text-xs font-bold ${
                  tge.status === 'OPEN' ? 'status-open' : 'status-closed'
                }`}>
                  {tge.status}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Launch Date:</span>
                  <div className="text-white">{tge.name === 'Unite' ? 'Jul 30, 2025' : tge.name === 'RHEA' ? 'Jul 30, 2025' : tge.name === 'Towns' ? 'Aug 5, 2025' : tge.name === 'Caesar' ? 'Aug 16, 2025' : tge.name === 'Gaia' ? 'Jul 30, 2025' : formatDate(tge.launchDate)}</div>
                </div>
                {tge.actualFDV && (
                  <div>
                    <span className="text-gray-400">Actual FDV:</span>
                    <div className="text-green-400">{formatCurrency(tge.actualFDV)}</div>
                  </div>
                )}
                <div>
                  <span className="text-gray-400">Total Bets:</span>
                  <div className="text-primary">{tge.totalBets || 0} chips</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel; 